import AppPanel from "@/features/invoices/order-confirmation-invoices/app-panel";
import { Box, Flex, Grid, GridItem, Skeleton, Text } from "@chakra-ui/react";
import { toTitle } from "@/utils/case";
import AppPriceInput, {
  PriceInputValue,
} from "@/features/invoices/app-price-input";
import React from "react";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import { Field, FieldProps, FormikProps } from "formik";
import { OrderConfirmationInvoiceFormFields } from "@/features/invoices/order-confirmation-invoices/order-confirmation-invoice-detail-page";

interface OrderConfirmationInvoiceFigureFormControlProps<T> {
  formik: FormikProps<T>;
  isEditing?: boolean;
  isDisabled?: boolean;
}

interface FormFields {
  id?: number;
  totalAmount?: PriceInputValue;
  totalRequiredAmount?: PriceInputValue;
  orderConfirmationInvoiceDetailList: OrderConfirmationInvoiceFormFields["orderConfirmationInvoiceDetailList"];
  isRemitted?: boolean;
  orderSheetSets?: OrderConfirmationInvoiceFormFields["orderSheetSets"];
  issuedBy?: OrderConfirmationInvoiceFormFields["issuedBy"];
}

export default function OrderConfirmationInvoiceFigureFormControl<
  T extends FormFields
>({
  formik,
  isEditing = false,
  isDisabled = false,
}: OrderConfirmationInvoiceFigureFormControlProps<T>) {
  const { t, tTitle } = useI18nHelper();

  if (!formik.values.id) {
    return (
      <AppPanel title={<Skeleton height={"32px"} />}>
        <Grid
          paddingRight={"12px"}
          gridTemplateColumns={"repeat(3, 1fr)"}
          gridTemplateRows={"20px 24px repeat(2, 20px 24px)"}
          gridColumnGap={"8px"}
          gridRowGap={"4px"}
        >
          <Box />
          <Skeleton />
          <Skeleton />

          <Skeleton />
          <Skeleton />
          <Skeleton />

          <Skeleton />
          <Skeleton />
          <Skeleton />

          <Skeleton />
          <Skeleton />
          <Skeleton />

          <Skeleton />
          <Skeleton />
          <Skeleton />

          <Skeleton gridColumn={"1/4"} />
        </Grid>
      </AppPanel>
    );
  }

  return (
    <AppPanel title={tTitle("common:invoice_figures")}>
      <Flex gap={"12px"} flexDirection={"column"}>
        {/* 브랜드별 금액·디파짓은 입력하지 않는다(서버가 총액을 연결 오더의 브랜드별 확정금액 비율로 나눠 저장) — 브랜드만 보여 준다 */}
        {formik.values.orderConfirmationInvoiceDetailList.length > 0 && (
          <Text fontSize={"12px"} color={"#8A8A86"}>
            {toTitle(t("brand"))}:{" "}
            {Array.from(
              new Set(
                formik.values.orderConfirmationInvoiceDetailList
                  .map((ocDetail) => ocDetail.brand?.name)
                  .filter((name): name is string => !!name)
              )
            ).join(", ")}
          </Text>
        )}
        <Grid
          gridTemplateColumns={"repeat(3, 1fr) 12px"}
          gridTemplateRows={"20px 24px"}
          gridColumnGap={"8px"}
          gridRowGap={"4px"}
        >
          <Box></Box>
          <GridItem fontSize={"12px"}>{toTitle(t("common:invoice_total_amount"))}</GridItem>
          <GridItem fontSize={"12px"}>
            {toTitle(t("total_deposit_required_amount.abbr"))}
          </GridItem>
          <Box></Box>

          <Box></Box>
          <GridItem>
            <Field name={"totalAmount"}>
              {(props: FieldProps<FormFields["totalAmount"], FormFields>) => {
                return (
                  <AppPriceInput
                    value={props.field.value ?? { currency: "EUR" }}
                    currency={"EUR"}
                    onChange={(value) => {
                      props.form.setFieldValue(props.field.name, value);
                    }}
                    isReadOnly={!isEditing}
                    isDisabled={isDisabled}
                  />
                );
              }}
            </Field>
          </GridItem>
          <GridItem>
            <Field name={"totalRequiredAmount"}>
              {(
                props: FieldProps<FormFields["totalRequiredAmount"], FormFields>
              ) => {
                return (
                  <AppPriceInput
                    value={props.field.value}
                    onChange={(value) => {
                      props.form.setValues((prev) => {
                        return {
                          ...prev,
                          totalRequiredAmount: value,
                        };
                      });
                    }}
                    isReadOnly={!isEditing}
                    isDisabled={isDisabled}
                  />
                );
              }}
            </Field>
          </GridItem>
        </Grid>
      </Flex>
    </AppPanel>
  );
}
